import DOMPurify from 'dompurify';

/**
 * Sanitizes publisher-authored HTML before it reaches `dangerouslySetInnerHTML`.
 *
 * Topic 04's "publisher HTML sanitized at the `dangerouslySetInnerHTML`
 * boundaries" item. The threat: article bodies, captions and home-page markup
 * are authored in the publisher's CMS and rendered as raw HTML, and this app
 * keeps auth tokens in `localStorage` — so a single stored `<img onerror=...>`
 * in any tenant's content reads every subscriber's session.
 *
 * **What this deliberately does NOT cover.** Three components execute
 * publisher `<script>` tags on purpose, for ad and embed revenue:
 * `custom.tsx` (via `createContextualFragment`, which runs scripts where
 * `innerHTML` would not), and `video.tsx`/`tik-tok.tsx` (via the
 * `dangerously-set-html-content` package). Sanitizing those would break
 * already-published content across live tenants, and deciding which publisher
 * scripts may run is a product call backed by a content audit nobody has done.
 * They are excluded by design, not by oversight — see `useUnsafeHtml`.
 *
 * **Why the config is permissive about `iframe` and `style`.** The default
 * DOMPurify profile strips both, and both are load-bearing here: publishers
 * embed players and widgets inline, and this product's entire styling model is
 * CMS-authored inline CSS. Stripping them would silently break paying
 * publishers' existing articles, which is a worse outcome than the narrow
 * residual risk of allowing them — DOMPurify still sanitizes `style` contents
 * and every URI attribute, so `javascript:` srcs and CSS escapes don't survive.
 * What matters for the actual XSS vector is unchanged from the default
 * profile: `<script>` and every `on*` event-handler attribute are removed.
 */
const SANITIZE_CONFIG = {
	ADD_TAGS: ['iframe'],
	ADD_ATTR: [
		'allow',
		'allowfullscreen',
		'frameborder',
		'scrolling',
		'loading',
		'referrerpolicy',
		'target',
	],
};

export function sanitizeHtml(html: string): string {
	// DOMPurify needs a real DOM. Everything that renders HTML runs in the
	// browser (or jsdom under test), but the library is meant to be portable,
	// so this degrades to dropping the markup rather than throwing if it's
	// ever imported somewhere without a `window` (a worker, SSR).
	if (typeof window === 'undefined') {
		return '';
	}
	return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}
